import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ChevronRight } from "lucide-react";
import type { RouterOutputs } from "@/lib/trpc";

/**
 * Stage 3.5 — "Connect to team direction" picker for the My-priorities loop.
 *
 * DESIGN NOTE (this is NOT Stage 3D):
 * 1. The Home surface this serves is a FLAT, SELF-ONLY My-priorities list —
 *    not a grouped Home summary.
 * 2. Connected-to tags are per PERSONAL priority (inverse orientation), never
 *    grouped or counted by team priority.
 * 3. No grouped/count-shaped endpoint exists or is introduced
 *    (mineGroupedByTeamPriority stays absent by barrier).
 * 4. Team Direction (the Team tab) remains the canonical team-direction
 *    surface; My priorities is the canonical personal-work surface.
 * 5. THIS PICKER's data source is the already-safe, member-gated
 *    `teamPriorities.list` (sanitized projection: no createdByUserId, no
 *    members, no counts, no who-linked, no progress). No new team-priority
 *    picker endpoint was introduced.
 * 6. The link action is the existing owner-only `personalPriorityLinks.link`
 *    (ids only; neutral denials preserved).
 * 7. An empty picker is a NEUTRAL fact ("No team direction available to
 *    connect.") — unlinked work is never framed as incomplete/noncompliant.
 */
export type PickableDirection = RouterOutputs["teamPriorities"]["list"][number];

export interface ConnectDirectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isAr: boolean;
  /** ACTIVE team-direction rows from the member-gated teamPriorities.list. */
  directions: PickableDirection[];
  loading?: boolean;
  busy?: boolean;
  /** The OWN personal-priority title being connected (context only). */
  personalTitle: string;
  onPick: (teamPriorityId: number) => void;
}

export function ConnectDirectionDialog({
  open,
  onOpenChange,
  isAr,
  directions,
  loading,
  busy,
  personalTitle,
  onPick,
}: ConnectDirectionDialogProps) {
  const t = (en: string, ar: string) => (isAr ? ar : en);
  const active = directions.filter(d => d.teamStatus === "active");
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="connect-direction">
        <DialogHeader>
          <DialogTitle>{t("Connect to team direction", "الربط بتوجيه الفريق")}</DialogTitle>
          <DialogDescription>
            {t(
              `Choose a team priority to connect “${personalTitle}” to. Only you can see this connection.`,
              `اختر أولوية فريق لربط "${personalTitle}" بها. أنت وحدك ترى هذا الارتباط.`,
            )}
          </DialogDescription>
        </DialogHeader>
        {active.length === 0 && !loading ? (
          <p className="text-sm text-muted-foreground" data-testid="connect-direction-empty">
            {t("No team direction available to connect.", "لا يوجد توجيه فريق متاح للربط.")}
          </p>
        ) : (
          <ul className="space-y-2" data-testid="connect-direction-list">
            {active.map(d => (
              <li key={d.id}>
                {/* Tappable CARD row (mobile-polish): bordered, padded, full
                    tap target, with a chevron affordance so it reads as
                    selectable rather than plain text. */}
                <button
                  type="button"
                  disabled={busy}
                  data-testid={`connect-pick-${d.id}`}
                  onClick={() => onPick(d.id)}
                  className="flex w-full items-center justify-between gap-3 rounded-xl border border-border/70 bg-card px-3.5 py-3 text-start hover:bg-muted/50 active:scale-[0.99] transition disabled:opacity-50"
                >
                  <span className="min-w-0 flex-1 text-sm font-medium break-words">{d.title}</span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
